import React, { useEffect, useState, useRef } from 'react';
import RecipeCard from '../components/RecipeCard';
import Masonry from 'react-masonry-css';
import { useGetAllFavoriteRecipes } from '../hooks/favoriteRecipe';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import { useAuth } from '../context/AuthContext';
import { Spinner, Form } from 'react-bootstrap';
import ErrorBoundary from '../components/ErrorBoundary';
import { getIngredientImageUrl } from '../utils/imageUtils';

const RecipesForIngredients = ({ onMealAdded }) => {
    const [allIngredients, setAllIngredients] = useState([]);
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [filteredIngredients, setFilteredIngredients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingIngredients, setLoadingIngredients] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const { sessionToken } = useAuth();
    const { getAllFavoriteRecipes } = useGetAllFavoriteRecipes();
    const { getAllDislikedRecipes } = useGetAllDislikedRecipes();
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const searchRef = useRef(null);

    const breakpointColumns = {
        default: 4,
        1100: 3,
        700: 2,
        400: 1
    };

    // Fetch all ingredients on mount
    useEffect(() => {
        if (!sessionToken) return;
        
        const fetchIngredients = async () => {
            try {
                const response = await fetch('/api/v1/ingredient/', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch ingredients: ${response.status}`);
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setAllIngredients(data);
                }
            } catch (error) {
                console.error('Error fetching ingredients:', error);
            } finally {
                setLoadingIngredients(false);
            }
        };

        fetchIngredients();
    }, [sessionToken]);

    // Fetch favorite and disliked recipes
    useEffect(() => {
        if (!sessionToken) return;
        
        const fetchFavoritesAndDislikes = async () => {
            try {
                // Fetch favorites
                const favoritesData = await getAllFavoriteRecipes(sessionToken);
                if (Array.isArray(favoritesData)) {
                    setFavoriteRecipes(favoritesData);
                }

                // Fetch dislikes
                const dislikesData = await getAllDislikedRecipes(sessionToken);
                if (Array.isArray(dislikesData)) {
                    setDislikedRecipes(dislikesData);
                }
            } catch (error) {
                console.error('Error fetching favorites/dislikes:', error);
            }
        };
        fetchFavoritesAndDislikes();
    }, [sessionToken, getAllFavoriteRecipes, getAllDislikedRecipes]);

    // Fetch recipes when selected ingredients change
    useEffect(() => {
        if (!sessionToken || selectedIngredients.length === 0) {
            setRecipes([]);
            return;
        }

        const fetchRecipes = async () => {
            setLoading(true);
            try {
                const ingredientIds = selectedIngredients.map(ing => ing.id).join(',');
                const response = await fetch(`/api/v1/recipe-ingredient/ingredients/${ingredientIds}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch recipes: ${response.status}`);
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setRecipes(data);
                    console.log("recipes: ", data);
                } else {
                    setRecipes([]);
                }
            } catch (error) {
                console.error('Error fetching recipes:', error);
                setRecipes([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipes();
    }, [selectedIngredients, sessionToken]);

    // Filter ingredients based on search term
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredIngredients([]);
            setShowDropdown(false);
        } else {
            const filtered = allIngredients
                .filter(ing => 
                    ing.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                    !selectedIngredients.some(selected => selected.id === ing.id)
                )
                .slice(0, 10); // Limit to 10 results
            setFilteredIngredients(filtered);
            setShowDropdown(filtered.length > 0);
        }
    }, [searchTerm, allIngredients, selectedIngredients]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectIngredient = (ingredient) => {
        setSelectedIngredients([...selectedIngredients, ingredient]);
        setSearchTerm('');
        setShowDropdown(false);
    };

    const handleRemoveIngredient = (ingredientId) => {
        setSelectedIngredients(selectedIngredients.filter(ing => ing.id !== ingredientId));
    };

    return (
        <ErrorBoundary fallbackMessage="Unable to load recipe search. Please try again later.">
            <div className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
                <h2>Search Recipes by Ingredients</h2>
                <p className="text-muted mb-4">
                    Select ingredients to find recipes that use all of them
                </p>

                {/* Search Bar */}
                <div className="mb-4" ref={searchRef} style={{ position: 'relative' }}>
                    <Form.Control
                        type="text"
                        placeholder="Search for an ingredient..."
                        className="form-control-custom"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={loadingIngredients}
                        style={{
                            borderRadius: '8px',
                            padding: '12px',
                            fontSize: '16px',
                            border: '2px solid #e0e0e0',
                        }}
                    />
                    
                    {/* Dropdown for filtered ingredients */}
                    {showDropdown && (
                        <div 
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                marginTop: '4px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                zIndex: 1000,
                            }}
                        >
                            {filteredIngredients.map(ingredient => (
                                <div
                                    key={ingredient.id}
                                    onClick={() => handleSelectIngredient(ingredient)}
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f0f0f0',
                                        transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                    {ingredient.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Selected Ingredients */}
                {selectedIngredients.length > 0 && (
                    <div className="mb-4">
                        {/* <h5>Selected Ingredients</h5> */}
                        <div className="d-flex flex-wrap gap-2">
                            {selectedIngredients.map(ingredient => (
                                <div
                                    key={ingredient.id}
                                    className="badge d-flex align-items-center p-2 selected-allergen-ingredient-badge"
                                >
                                    <img 
                                        src={getIngredientImageUrl(ingredient.id)}
                                        alt={`${ingredient.name} icon`}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                        style={{ width: '25px', height: '25px', marginRight: '5px' }}
                                    />
                                    <span>{ingredient.name}</span>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white ms-2"
                                        style={{ fontSize: '0.5rem' }}
                                        onClick={() => handleRemoveIngredient(ingredient.id)}
                                        aria-label="Remove"
                                    ></button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Recipes Display */}
                {loading ? (
                    <div className="text-center my-4">
                        <Spinner role="status" style={{ color: '#52cd92' }}>
                            <span className="visually-hidden">Loading recipes...</span>
                        </Spinner>
                    </div>
                ) : selectedIngredients.length > 0 && recipes.length === 0 ? (
                    <div className="alert alert-warning" role="alert">
                        <p className="mb-0">No recipes found that use all selected ingredients. Try removing some ingredients.</p>
                    </div>
                ) : recipes.length > 0 ? (
                    <>
                        <h4 className="mb-3">Found {recipes.length} recipe{recipes.length !== 1 ? 's' : ''}</h4>
                        <Masonry
                            breakpointCols={breakpointColumns}
                            className="my-masonry-grid"
                            columnClassName="my-masonry-grid_column"
                        >
                            {recipes.map(recipe => (
                                <RecipeCard 
                                    key={recipe.id} 
                                    recipe={recipe} 
                                    isFavorite={Array.isArray(favoriteRecipes) && favoriteRecipes.includes(Number(recipe.id))} 
                                    isDisliked={Array.isArray(dislikedRecipes) && dislikedRecipes.includes(Number(recipe.id))}
                                    onMealAdded={onMealAdded}
                                />
                            ))}
                        </Masonry>
                    </>
                ) : null}
            </div>
        </ErrorBoundary>
    );
};

export default RecipesForIngredients;

